import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Comment, ContextType, ConversationAPI } from 'api/conversations';
import { ProjectConversationId, ProjectId } from 'api/models';
import { ConversationMember } from 'api/project-conversations';
import { ConversationPane } from 'components/organisms/conversations';
import { useProjectDetail } from 'data/projects';
import { useAccount } from 'hooks/use-account';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useUpdateMembersLastSeenMutation } from 'queries/project-wide-conversation';
import { useActiveProjectConversation } from '../../proj-conversation-hooks';
import { ChannelDetails } from '../channel-details';
import { CommentSeenBy } from './comment-seen-by';
import { JoinChannel } from './join-conversation';
import { ConvPaneWrapper } from './styles';

interface Props {
  projectId: ProjectId;
  isUserMemberOfChannel?: boolean;
}

export const ProjectConversationPane: FC<Props> = ({
  projectId,
  isUserMemberOfChannel,
}) => {
  const {
    user: { userId },
  } = useUser();
  const activeConversation = useActiveProjectConversation();
  const { data: project } = useProjectDetail(projectId);

  const [userMessageContent, _] = useState('');

  const commentBoxPlaceholder = useTranslation(
    'projects.conversations.placeholder',
    {
      conversationName: activeConversation?.conversationName || '',
      projectName: project?.projectName,
    },
  );

  // useEffect(() => {
  //   // Now we're just toggling to cause a re-render, eventually this can store a conversation level draft data.
  //   setUserMessageContent('-');
  //   setUserMessageContent('');
  // }, [activeConversation?.conversationId]);

  const { accountId } = useAccount();
  // const [userComments, setUserComments] = useState<Comment[]>([]);
  const memberDetails = (activeConversation?.members || []).find(
    (memberObject) => memberObject.user.userId === userId,
  );

  const currentLastReadCommentId = memberDetails?.lastSeenCommentId;
  // Store the older commentId between renders so that when currentLastReadCommentId changes through socket,
  // we can still refer the old lastReadCommentId.
  const lastReadComment =
    useRef<
      | undefined
      | {
          commentId: number;
          convId: number | undefined;
        }
    >();

  useEffect(() => {
    if (!currentLastReadCommentId) {
      return;
    }

    if (
      !lastReadComment.current?.commentId ||
      lastReadComment.current?.convId !== activeConversation?.conversationId
    ) {
      lastReadComment.current = {
        commentId: currentLastReadCommentId,
        convId: activeConversation?.conversationId,
      };
    }
  }, [currentLastReadCommentId, activeConversation?.conversationId]);

  // const currentUserLastReadCommentIndex = userComments.findIndex(
  //   (cmnt) => cmnt.commentId === lastReadComment?.commentId,
  // );
  // const tailMessages = userComments.slice(currentUserLastReadCommentIndex + 1);
  // No need to show the hook if the lastMessages were from the activeUser itself
  // const shouldRenderNewMessagesMarker = tailMessages.find(
  //   (cmnt) => cmnt.user.userId !== userId,
  // );

  // const lastComment = userComments[userComments.length - 1];

  // consComment = [...userComments]
  //   .reverse()
  //   .find((comment) => comment.messageType === MessageType.UserMessage);

  // const onConversationCommentsLoaded = useCallback((comments: Comment[]) => {
  //   if (comments.length) {
  //     setUserComments(comments);
  //   }
  // }, []);

  // const isTabVisible = usePageVisibility();

  const { mutate: mutateMembersLastSeen } =
    useUpdateMembersLastSeenMutation(projectId);

  const lastSeenUsersForComments = useMemo(() => {
    const returnValue = {};

    activeConversation?.members?.forEach((member) => {
      if (member.lastSeenCommentId && member.user.userId !== userId) {
        // returnValue.set(member?.lastSeenCommentId, [
        //   ...(returnValue.get(member?.lastSeenCommentId) ?? []),
        //   member.user.userId,
        // ]);
        if (member.lastSeenCommentId) {
          returnValue[member?.lastSeenCommentId] = [
            ...(returnValue[member?.lastSeenCommentId] ?? []),
            member,
          ];
        }
      }
    });

    return returnValue;
  }, [activeConversation?.members, userId]);

  // useEffect(() => {
  //   const isLastCommentLoaded =
  //     lastUserComment &&
  //     lastUserComment.commentContext?.id === activeConversation?.conversationId;

  //   const currentLastCommentId = lastUserComment?.commentId;

  //   if (!isTabVisible) {
  //     return;
  //   }

  //   if (currentLastReadCommentId === currentLastCommentId) {
  //     return;
  //   }

  //   if (activeConversation?.conversationId && isLastCommentLoaded) {
  //     mutateMembersLastSeen({
  //       payload: {
  //         commentId: currentLastCommentId,
  //       },
  //       conversationId: ProjectConversationId(
  //         activeConversation.conversationId,
  //       ),
  //     });
  //   }
  // }, [
  //   activeConversation?.conversationId,
  //   currentLastReadCommentId,
  //   mutateMembersLastSeen,
  //   lastUserComment,
  //   isTabVisible,
  // ]);

  const lastSeenCallback = useCallback(
    (lastUserComment: Comment) => {
      const isLastCommentLoaded =
        lastUserComment &&
        lastUserComment.commentContext?.id ===
          activeConversation?.conversationId;

      if (lastUserComment?.commentId === currentLastReadCommentId) {
        return;
      }
      if (
        mutateMembersLastSeen &&
        activeConversation?.conversationId &&
        isLastCommentLoaded
      ) {
        mutateMembersLastSeen({
          payload: {
            commentId: lastUserComment.commentId,
          },
          conversationId: ProjectConversationId(
            activeConversation.conversationId,
          ),
        });
      }
    },
    [
      activeConversation?.conversationId,
      mutateMembersLastSeen,
      currentLastReadCommentId,
    ],
  );

  // const renderMessageFooterExtra = useCallback(
  //   (comment) => {
  //     return shouldRenderNewMessagesMarker &&
  //       lastComment?.commentId !== lastReadComment?.commentId &&
  //       currentUserLastReadCommentIndex > -1 ? (
  //       <MessageFooter
  //         currentComment={comment}
  //         lastReadCommentId={lastReadComment?.commentId}
  //       />
  //     ) : null;
  //   },
  //   [
  //     lastReadComment,
  //     lastComment,
  //     shouldRenderNewMessagesMarker,
  //     currentUserLastReadCommentIndex,
  //   ],
  // );

  const newRenderMessageTailComponent = useCallback(
    (comment: Comment, lastSeenUsers?: ConversationMember[]) => {
      return <CommentSeenBy seenUsers={lastSeenUsers ?? []} />;
    },
    [],
  );

  // const renderMessageTailComponent = useCallback(
  //   (currentComment: Comment) => {
  //     if (!activeConversation?.members) {
  //       return null;
  //     }

  //     const seenUsers =
  //       activeConversation.members.filter(
  //         (member) =>
  //           userId !== member.user.userId &&
  //           currentComment.commentId &&
  //           currentComment.commentId === member.lastSeenCommentId,
  //       ) ?? [];

  //     return seenUsers.length ? <CommentSeenBy seenUsers={seenUsers} /> : null;
  //   },
  //   [activeConversation?.members, userId],
  // );

  const contextObj = useMemo(() => {
    return {
      type: 'PROJECT_CONVERSATIONS' as keyof typeof ContextType,
      id: activeConversation?.conversationId ?? -1,
    };
  }, [activeConversation?.conversationId]);

  const renderMessagesComponent = useCallback(() => {
    return (
      !isUserMemberOfChannel &&
      activeConversation && (
        <JoinChannel conversation={activeConversation} projectId={projectId} />
      )
    );
  }, [activeConversation, isUserMemberOfChannel, projectId]);

  const messageInitialComponent = useMemo(() => {
    return activeConversation?.createdAt ? (
      <ChannelDetails
        createdAt={activeConversation?.createdAt}
        createdBy={activeConversation?.createdBy}
        conversationDescription={activeConversation?.conversationDescription}
        conversationId={activeConversation?.conversationId}
        conversationName={activeConversation?.conversationName}
      />
    ) : null;
  }, [
    activeConversation?.createdAt,
    activeConversation?.createdBy,
    activeConversation?.conversationDescription,
    activeConversation?.conversationId,
    activeConversation?.conversationName,
  ]);

  const commentBoxConfig = useMemo(() => {
    return {
      className: 'project-conversation-comment-box',
      placeholder: commentBoxPlaceholder,
      hidePrivate: activeConversation?.private,
    };
  }, [activeConversation?.private, commentBoxPlaceholder]);

  return (
    <ConvPaneWrapper isUserMemberOfChannel={Boolean(isUserMemberOfChannel)}>
      {activeConversation?.conversationId ? (
        <ConversationPane
          messagesInitialComponent={messageInitialComponent}
          // renderMessageFooterExtra={renderMessageFooterExtra}
          renderMessageTailComponent={newRenderMessageTailComponent}
          // onCommentsLoaded={onConversationCommentsLoaded}
          messagesComponent={renderMessagesComponent()}
          userMessageContent={userMessageContent}
          commentBoxConfig={commentBoxConfig}
          context={contextObj}
          projectId={projectId[1]}
          accountId={accountId}
          apiService={ConversationAPI}
          updateLastSeen={false} // we will handle last seen update within this component. So child component doesn't need to. This is in contradictory with global conversation where we need to handle inside the child component
          lastSeenUserForComments={lastSeenUsersForComments}
          lastSeenUpdateCallback={lastSeenCallback}
        />
      ) : null}
    </ConvPaneWrapper>
  );
};

// previously the last seen was handled in projects in the parent component.But this causes more re renders.So moving this to the child component with lastSeenupdateCallback. The existing updateLastSeen is deeply tied with
// global conv, so not touching that for now.
